/**
 * Which tool a key or a rail button means — `docs/property-maps-v2.md` §5.3.
 *
 * The rail, the keyboard and the phone's drawing bar all choose from this one
 * table, so a tool cannot exist in two of them and not the third. `select`
 * and `pan` are modes rather than tools: neither makes a shape, so neither
 * has a class.
 */
import type { FeatureKind } from '@rowted/shared'
import { PathTool } from './area.js'
import { DragTool } from './rect.js'
import { PointTool } from './marker.js'
import type { DrawTool, ToolOptions } from './types.js'

export type ToolName = 'select' | 'pan' | FeatureKind | 'measure'

/** §5.3's keyboard: V H A R C L M T U. */
export const TOOL_KEYS: Record<string, ToolName> = {
  v: 'select',
  h: 'pan',
  a: 'area',
  r: 'rect',
  c: 'circle',
  l: 'line',
  m: 'marker',
  t: 'label',
  u: 'measure',
}

/** Null for the modes, which draw nothing. */
export function toolFor(name: ToolName, options: ToolOptions): DrawTool | null {
  switch (name) {
    case 'area':
    case 'line':
      return new PathTool(name, options)
    case 'rect':
    case 'circle':
      return new DragTool(name, options)
    case 'marker':
    case 'label':
      return new PointTool(name, options)
    default:
      return null
  }
}

export { PathTool } from './area.js'
export { DragTool, cornersOf } from './rect.js'
export { PointTool } from './marker.js'
export { snapper, nearestOnSegment, SNAP_PX } from './snap.js'
export type { Draft, DrawEvent, DrawTool, KeyEvent, MapFrame, ScreenPoint, ToolOptions }
  from './types.js'
